import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { brand, gray } from "../../../theme/themePrimitives";
import { useNavigate } from "react-router-dom";
import useScreenSize from "../../../hooks/useScreenSize";

interface AcknowledgementPanelProps {
  unlocked: boolean;
  unlockHint: string;
  onSubmit: (decision: boolean, comment: string) => Promise<void>;
  // Prior submission hydrated from the backend (or just recorded by
  // onSubmit) — once set, the response is locked and cannot be resubmitted.
  submittedAck: { decision: boolean; comment: string } | null;
  alreadyCompleted?: boolean;
}

export const AcknowledgementPanel: React.FC<AcknowledgementPanelProps> = ({
  unlocked,
  unlockHint,
  onSubmit,
  submittedAck,
  alreadyCompleted = false,
}) => {
  const [decision, setDecision] = useState<boolean | null>(
    submittedAck?.decision ?? null,
  );
  const [comment, setComment] = useState(submittedAck?.comment ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const submitted = !!submittedAck;
  const controlsDisabled = !unlocked || submitted || isSubmitting;

  useEffect(() => {
    if (submittedAck) {
      setDecision(submittedAck.decision);
      setComment(submittedAck.comment);
    }
  }, [submittedAck]);

  useEffect(() => {
    if (unlocked) setExpanded(true);
  }, [unlocked]);

  const commentRequired = decision === false;
  const commentError = commentRequired && comment.trim().length === 0;
  const isDisabled = controlsDisabled || decision == null;
  const canSubmit = !isDisabled && !commentError;

  const navigate = useNavigate();
  const { isTablet } = useScreenSize();

  // Shared header copy for tablet's compact bar and desktop/mobile's
  // stacked title block, driven by unlocked/submitted/alreadyCompleted state.
  const headerTitle = submitted
    ? "Acknowledgement submitted"
    : !unlocked
      ? alreadyCompleted
        ? "Acknowledgement not required"
        : "Acknowledgement locked"
      : "Acknowledgement required";

  const headerSubtitle = submitted
    ? "Your response has been recorded and cannot be changed."
    : !unlocked
      ? alreadyCompleted
        ? "This assignment was completed before acknowledgement was required, so no response is needed."
        : unlockHint
      : "Please confirm your response before completing this assignment.";

  const headerIcon =
    unlocked || submitted || alreadyCompleted ? (
      <CheckCircleOutlineIcon
        sx={{
          color: alreadyCompleted
            ? "#667085"
            : unlocked && !submitted
              ? brand[50]
              : brand[600],
          fontSize: isTablet ? 15 : 22,
        }}
      />
    ) : (
      <LockOutlinedIcon
        sx={{ color: "#667085", fontSize: isTablet ? 16 : 22 }}
      />
    );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (commentError) {
      setAttemptedSubmit(true);
      return;
    }
    try {
      setIsSubmitting(true);
      await onSubmit(decision, comment);
      navigate("/home");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formFields = (
    <>
      <Stack sx={{ p: "16px", gap: 2 }}>
        <RadioGroup
          value={decision === null ? "" : decision ? "yes" : "no"}
          onChange={(e) => setDecision(e.target.value === "yes")}
          sx={{
            display: "flex",
            flexDirection: isTablet ? "row" : "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              backgroundColor: decision === true ? brand[50] : "#F9FAFB",
              borderRadius: "12px",
              border:
                decision === true
                  ? `2px solid ${brand[600]}`
                  : "1px solid #EAECF0",
              p: 2,
              mb: 1,
            }}
          >
            <FormControlLabel
              value="yes"
              control={
                <Radio
                  disabled={controlsDisabled}
                  sx={{ "&.Mui-checked": { color: brand[600] } }}
                />
              }
              label="I acknowledge and understand this content"
              disabled={controlsDisabled}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              backgroundColor: decision === false ? brand[50] : "#F9FAFB",
              borderRadius: "12px",
              border:
                decision === false
                  ? `2px solid ${brand[600]}`
                  : "1px solid #EAECF0",
              p: 2,
              mb: 1,
            }}
          >
            <FormControlLabel
              value="no"
              control={
                <Radio
                  disabled={controlsDisabled}
                  sx={{ "&.Mui-checked": { color: brand[600] } }}
                />
              }
              label="I do not acknowledge this content"
              disabled={controlsDisabled}
            />
          </Box>
        </RadioGroup>

        <Box>
          <Stack direction="row" alignItems="center" gap={0.5} mb={0.5}>
            <Typography fontSize={14} color="text.secondary" fontWeight={600}>
              Comment
            </Typography>
            <Tooltip
              title="Required if you decline, optional if you acknowledge."
              arrow
              placement="bottom"
              slotProps={{
                tooltip: { sx: { bgcolor: "#1F2937", color: "#fff" } },
                arrow: { sx: { color: "#1F2937" } },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, color: gray[500] }} />
            </Tooltip>
          </Stack>
          <TextField
            placeholder="Add a comment..."
            required={commentRequired}
            error={commentError && attemptedSubmit}
            multiline
            minRows={4}
            fullWidth
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={controlsDisabled}
            sx={{
              backgroundColor: gray[50],
              borderColor: gray[300],
            }}
          />
        </Box>
      </Stack>
    </>
  );

  const footer = (
    <Stack
      sx={{
        p: "16px",
        gap: 1,
        borderTop: "1px solid #D0D5DD",
        background: "#fff",
      }}
    >
      {isSubmitting ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Button
          disabled={isDisabled}
          onClick={handleSubmit}
          sx={{
            backgroundColor: !canSubmit ? gray[50] : `${brand[600]} !important`,
            border: !canSubmit ? `1px solid ${gray[300]}` : "none",
            color: !canSubmit ? gray[400] : "#fff !important",
          }}
        >
          Submit response
        </Button>
      )}
    </Stack>
  );

  // Tablet gets a compact, always-visible header (icon + title + status,
  // with a chevron to expand/collapse the form) instead of the larger
  // stacked title block used on mobile/desktop.
  if (isTablet) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "sticky",
          bottom: 0,
          zIndex: 10,
          background: "#fff",
          borderRadius: "12px 12px 0 0",
          boxShadow: "0px -4px 12px rgba(16, 24, 40, 0.08)",
          borderWidth: " 1px 1px 0px 1px",
          borderStyle: "solid",
          borderColor: "#CBD8FA",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            p: "12px 16px",
            cursor: "pointer",
            height: expanded ? 112 : 73,
            background: brand[50],
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor:
                  submitted || !unlocked ? "#F2F4F7" : brand[600],
                border: "1px solid #D0D5DD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {headerIcon}
            </Box>
            <Stack>
              <Typography fontSize={15} fontWeight={600} whiteSpace="nowrap">
                {headerTitle}
              </Typography>
              {expanded && (
                <Typography
                  fontSize={13}
                  color="text.secondary"
                  noWrap
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {headerSubtitle}
                </Typography>
              )}
            </Stack>
          </Stack>
          <ExpandMoreIcon
            sx={{
              color: gray[500],
              transform: expanded ? "none" : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          />
        </Stack>
        <Divider />
        <Collapse in={expanded}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ maxHeight: "60vh", overflow: "auto" }}>{formFields}</Box>
            {footer}
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", background: "#fff" }}>
        {unlocked && !submitted ? (
          <>
            <Stack gap="12px" sx={{ textAlign: "center", p: "16px 8px" }}>
              <Typography fontSize={18} fontWeight={600}>
                {headerTitle}
              </Typography>
              <Typography fontSize={14} color="text.secondary">
                {headerSubtitle}
              </Typography>
            </Stack>
            <Divider />
          </>
        ) : (
          <>
            <Stack gap="12px" sx={{ textAlign: "center", p: "16px 8px" }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#F2F4F7",
                  border: "1px solid #D0D5DD",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mt: 2,
                }}
              >
                {headerIcon}
              </Box>
              <Typography fontSize={16} fontWeight={600}>
                {headerTitle}
              </Typography>
              <Typography fontSize={14} color="text.secondary">
                {headerSubtitle}
              </Typography>
            </Stack>
            <Divider />
          </>
        )}

        {formFields}
      </Box>
      {footer}
    </Box>
  );
};
